import { usePermissionsStore } from '@/stores/permissions';

interface DashboardCollectorenGeleverdBoxProps {
  /** Werf-level "collectoren al geleverd" flag (aggregated across fases). */
  collectorenGeleverd: boolean;
  /** Called with the next value when the toggle is flipped. */
  onToggle: (next: boolean) => void;
  /** Disables the toggle while a save is in flight. */
  saving?: boolean;
}

/**
 * Werf-level "collectoren al geleverd" status box — one project-wide flag.
 * Read-only badge for roles without collector_edit; a toggle otherwise.
 * Rendered under the Notities box in the dashboard (not the Collectoren section).
 */
export function DashboardCollectorenGeleverdBox({
  collectorenGeleverd,
  onToggle,
  saving = false,
}: DashboardCollectorenGeleverdBoxProps) {
  // Mirror the Collectoren section gate: editing requires collector_edit.
  const canEdit = usePermissionsStore((s) => s.canFeature)('collector_edit');

  // Nothing to show for read-only roles when the flag is off.
  if (!collectorenGeleverd && !canEdit) return null;

  return (
    <div
      className={`mb-4 flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 ${
        collectorenGeleverd
          ? 'border-td-green/40 bg-green-50'
          : 'border-gray-200 bg-gray-50'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={`text-base ${collectorenGeleverd ? 'text-td-green' : 'text-td-muted'}`} aria-hidden="true">
          {collectorenGeleverd ? '✓' : '📦'}
        </span>
        <span className={`text-sm font-semibold ${collectorenGeleverd ? 'text-td-green' : 'text-td-navy'}`}>
          {collectorenGeleverd
            ? 'Collectoren voor deze werf al geleverd'
            : 'Collectoren nog niet geleverd voor deze werf'}
        </span>
      </div>
      {canEdit && (
        <button
          type="button"
          role="switch"
          aria-checked={collectorenGeleverd}
          aria-label="Collectoren geleverd voor deze werf"
          disabled={saving}
          onClick={() => onToggle(!collectorenGeleverd)}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition disabled:opacity-50 ${
            collectorenGeleverd ? 'bg-td-green' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
              collectorenGeleverd ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </button>
      )}
    </div>
  );
}
